/**
 * StatusIsland - Bottom floating pill showing operational metrics
 *
 * Displays at-a-glance stats: aircraft count, occupancy %, arrivals, departures.
 * Each segment is clickable to toggle the corresponding filter preset.
 * Phase 1: collapsed only (expanded with detail view in Phase 2).
 */

import { useMemo } from 'react'
import { Plane, Building2, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Island } from './Island'
import { useRampFilterStore, type FilterPreset } from '@/stores/rampFilterStore'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface StatusIslandProps {
  enrichedData: Map<string, AllocationEnrichedData>
  totalUnits: number
  minimized?: boolean
}

interface StatusMetric {
  icon: React.ElementType
  value: number | string
  label: string
  filterPreset?: FilterPreset
  alert?: boolean
}

function isToday(date: Date): boolean {
  const now = new Date()
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

export function StatusIsland({ enrichedData, totalUnits, minimized }: StatusIslandProps) {
  const { activePresets, togglePreset } = useRampFilterStore()

  const metrics = useMemo<StatusMetric[]>(() => {
    let aircraftCount = 0
    let occupiedUnits = 0
    let arrivingToday = 0
    let departingToday = 0

    const occupiedUnitIds = new Set<string>()

    for (const [, data] of enrichedData) {
      aircraftCount++

      // Track occupied units
      if (data.allocation?.unitId) {
        occupiedUnitIds.add(data.allocation.unitId)
      }

      // Arriving today (transient check-in today)
      if (data.transientStay?.checkIn) {
        const checkIn = new Date(data.transientStay.checkIn)
        if (isToday(checkIn)) arrivingToday++
      }

      // Departing today (transient expected checkout today)
      if (data.transientStay?.expectedCheckOut) {
        const checkOut = new Date(data.transientStay.expectedCheckOut)
        if (isToday(checkOut)) departingToday++
      }
    }

    occupiedUnits = occupiedUnitIds.size
    const occupancyPct = totalUnits > 0
      ? Math.round((occupiedUnits / totalUnits) * 100)
      : 0

    return [
      { icon: Plane, value: aircraftCount, label: 'on site' },
      { icon: Building2, value: `${occupancyPct}%`, label: 'occ' },
      { icon: ArrowDownToLine, value: arrivingToday, label: 'arriving', filterPreset: undefined },
      { icon: ArrowUpFromLine, value: departingToday, label: 'departing', filterPreset: 'departing_today' },
    ]
  }, [enrichedData, totalUnits])

  return (
    <Island
      minimized={minimized}
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 px-1 py-1"
    >
      <div className="flex items-center gap-0">
        {metrics.map((metric, i) => {
          const isActive = metric.filterPreset && activePresets.has(metric.filterPreset)
          return (
            <button
              key={i}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors',
                'hover:bg-accent/50',
                isActive && 'bg-accent text-accent-foreground',
                metric.alert && 'text-destructive',
                i > 0 && 'border-l border-border/50'
              )}
              onClick={() => metric.filterPreset && togglePreset(metric.filterPreset)}
              title={`${metric.value} ${metric.label}`}
            >
              <metric.icon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-medium tabular-nums">{metric.value}</span>
            </button>
          )
        })}
      </div>
    </Island>
  )
}
